/**
 * RepConnect - Mobile Bottom Navigation
 * 
 * Copyright (c) 2025 RepConnect, LLC
 * 
 * Copyright (c) 2025 RepConnect, LLC
 * 
 * This software and associated documentation files are the proprietary
 * property of RepConnect, LLC.
 * All rights reserved.
 */

import React from 'react';
import {
  Home, Search, Calendar, User, Plus,
  Bell, Settings, BarChart3, Users,
  FileText, Shield, Clock, Coffee, CalendarCheck,
  Utensils, History
} from 'lucide-react';

const MobileNavigation = ({
  userType,
  currentView,
  setCurrentView,
  notifications = 0
}) => {

  const getSalesNavItems = () => [
    {
      id: 'dashboard',
      icon: Home,
      label: 'Home',
      view: 'dashboard'
    },
    {
      id: 'find',
      icon: Search,
      label: 'Providers',
      view: 'find'
    },
    {
      id: 'meetings',
      icon: Calendar,
      label: 'Calendar',
      view: 'meetings'
    },
    {
      id: 'orders',
      icon: Coffee,
      label: 'Orders',
      view: 'order-management'
    },
    {
      id: 'profile',
      icon: User,
      label: 'Settings',
      view: 'settings'
    }
  ];

  const getCustomerNavItems = (pendingCount = 0) => [
    {
      id: 'dashboard',
      icon: Home,
      label: 'Home',
      view: 'dashboard'
    },
    {
      id: 'dining',
      icon: Utensils,
      label: 'Dining',
      view: 'dining-preferences'
    },
    {
      id: 'calendar',
      icon: Calendar,
      label: 'Calendar',
      view: 'meetings',
      badge: pendingCount > 0 ? pendingCount : null // Only show badge when there are pending requests
    },
    {
      id: 'history',
      icon: History,
      label: 'History',
      view: 'history'
    },
    {
      id: 'availability',
      icon: Clock,
      label: 'Hours',
      view: 'availability'
    }
  ];

  const getManagerNavItems = () => [
    {
      id: 'dashboard',
      icon: Home,
      label: 'Home',
      view: 'dashboard'
    },
    {
      id: 'team',
      icon: Users,
      label: 'Team',
      view: 'team'
    },
    {
      id: 'analytics',
      icon: BarChart3,
      label: 'Analytics',
      view: 'analytics'
    },
    {
      id: 'compliance',
      icon: Shield,
      label: 'Reports',
      view: 'compliance'
    },
    {
      id: 'settings',
      icon: Settings,
      label: 'Settings',
      view: 'settings'
    }
  ];

  const getAdminNavItems = () => [
    {
      id: 'dashboard',
      icon: Home,
      label: 'Home',
      view: 'dashboard'
    },
    {
      id: 'meetings',
      icon: Calendar,
      label: 'Meetings',
      view: 'meetings'
    },
    {
      id: 'availability',
      icon: Settings,
      label: 'Schedule',
      view: 'availability'
    },
    {
      id: 'reports',
      icon: FileText,
      label: 'Reports',
      view: 'reports'
    }
  ];

  const getNavItems = () => {
    switch (userType) {
      case 'sales':
        return getSalesNavItems();
      case 'customer':
      case 'office_staff':
        return getCustomerNavItems(notifications); // Pass pending request count
      case 'manager':
        return getManagerNavItems();
      case 'admin':
        return getAdminNavItems();
      default:
        return getSalesNavItems();
    }
  };

  const navItems = getNavItems();

  // Handle navigation with scroll to top
  const handleNavClick = (view) => {
    // Comprehensive scroll reset for iOS Capacitor
    const scrollToTop = () => {
      // Method 1: Target app-scroll-container (the actual scroll container in App.js)
      const appScrollContainer = document.getElementById('app-scroll-container');
      if (appScrollContainer) {
        appScrollContainer.scrollTop = 0;
      }

      // Method 2: Target #root (Capacitor iOS scroll container)
      const rootElement = document.getElementById('root');
      if (rootElement) {
        rootElement.scrollTop = 0;
      }

      // Method 3: Standard scroll methods
      window.scrollTo(0, 0);
      document.documentElement.scrollTop = 0;
      document.body.scrollTop = 0;

      // Method 4: Target main element
      const mainElement = document.querySelector('main');
      if (mainElement) {
        mainElement.scrollTop = 0;
      }

      // Method 5: Scroll anchor into view (most reliable for iOS)
      const scrollAnchor = document.getElementById('scroll-anchor');
      if (scrollAnchor) {
        scrollAnchor.scrollIntoView(true);
      }
    };

    // Scroll BEFORE changing view
    scrollToTop();

    // Change the view
    setCurrentView(view);

    // Scroll again after view change with multiple timing attempts
    requestAnimationFrame(scrollToTop);
    setTimeout(scrollToTop, 0);
    setTimeout(scrollToTop, 50);
    setTimeout(scrollToTop, 100);
    setTimeout(scrollToTop, 200);
  };

  return (
    <nav
      className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 z-50"
      style={{ paddingBottom: 'env(safe-area-inset-bottom, 0px)' }}
    >
      <div className="flex" style={{ height: '49px' }}>
        {navItems.map((item) => {
          const Icon = item.icon;
          const isActive = currentView === item.view;

          return (
            <button
              key={item.id}
              onClick={() => handleNavClick(item.view)}
              className={`flex-1 flex flex-col items-center justify-center px-1 relative touch-manipulation select-none ${
                isActive
                  ? 'text-blue-600 bg-blue-50'
                  : 'text-gray-500 active:bg-gray-100'
              }`}
              style={{ WebkitTapHighlightColor: 'transparent' }}
              aria-label={item.label}
              aria-current={isActive ? 'page' : undefined}
            >
              <div className="relative pointer-events-none">
                <Icon className="w-5 h-5" />
                {/* Notification Badge */}
                {item.badge && item.badge > 0 && (
                  <div className="absolute -top-1.5 -right-2 bg-red-500 text-white text-xs rounded-full min-w-[16px] h-[16px] flex items-center justify-center font-bold px-0.5">
                    {item.badge > 99 ? '99+' : item.badge}
                  </div>
                )}
              </div>
              <span className={`text-[10px] mt-0.5 font-medium pointer-events-none ${isActive ? 'font-semibold' : ''}`}>
                {item.label}
              </span>
            </button>
          );
        })}
      </div>
    </nav>
  );
};

export default MobileNavigation;